// Loading + error feedback surfaces, plus a shared `formatError` so every
// page renders an ApiError the same way (`code: message`) instead of each
// re-deriving the instanceof ladder.
import { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { ApiError } from '../../lib/api';
import { cn } from './cn';
import { Card } from './Card';

export function Spinner({ className }: { readonly className?: string }) {
  return <Loader2 className={cn('animate-spin', className)} aria-hidden="true" />;
}

export function LoadingCard({ label = 'Loading…' }: { readonly label?: string }) {
  return (
    <Card className="p-8 flex items-center justify-center gap-3 text-fg-muted">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      <span className="text-sm">{label}</span>
    </Card>
  );
}

export function formatError(error: unknown): string {
  if (error instanceof ApiError) return `${error.code}: ${error.message}`;
  if (error instanceof Error) return error.message;
  return 'Unknown error';
}

export interface ErrorStateProps {
  readonly error: unknown;
  readonly hint?: ReactNode;
  readonly title?: string;
}

export function ErrorState({ error, hint, title = 'request failed' }: ErrorStateProps) {
  return (
    <Card role="alert" className="p-6 border-danger/40 bg-danger/5">
      <p className="orb-eyebrow text-danger mb-2">{title}</p>
      <p className="text-sm text-fg">{formatError(error)}</p>
      {hint ? <p className="text-xs text-fg-muted mt-2">{hint}</p> : null}
    </Card>
  );
}
